import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type View as RNView } from 'react-native';
import {
    AnimatePresence,
    ArrowRight,
    BodySmallEmphasized,
    BodySmallRegular,
    Icon,
    View,
    XStack,
    YStack,
} from 'smoothie';

const ARROW_SIZE = 10;
const TOOLTIP_GAP = 4;
const BUBBLE_WIDTH_FRACTION = 0.95;

export const GuidedTooltip = ({
    children,
    title = '',
    content,
    isActive,
    onNext,
    isLast = false,
    placement = 'bottom',
    arrowPercent,
    verticalOffset = 0,
    onBecameVisible,
    autoScroll = true,
    delay = 0,
}: {
    children: ReactNode;
    title?: string;
    content: string;
    isActive: boolean;
    onNext: () => void;
    isLast?: boolean;
    placement?: 'top' | 'bottom';
    arrowPercent?: number;
    verticalOffset?: number;
    onBecameVisible?: () => void;
    autoScroll?: boolean;
    delay?: number;
}) => {
    const tooltipRef = useRef<RNView>(null);
    const visibleFiredRef = useRef(false);

    const [showBubble, setShowBubble] = useState(false);
    const [inViewport, setInViewport] = useState(false);

    useEffect(() => {
        if (!isActive) {
            setInViewport(false);
            return;
        }
        const node = tooltipRef.current as unknown as HTMLElement | null;
        if (node == null) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setInViewport(true);
                    if (!visibleFiredRef.current) {
                        visibleFiredRef.current = true;
                        onBecameVisible?.();
                    }
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(node);
        return () => {
            observer.disconnect();
        };
    }, [isActive, onBecameVisible]);

    useEffect(() => {
        if (!isActive) {
            setShowBubble(false);
            return;
        }
        if (!inViewport) return;
        if (delay == null || delay <= 0) {
            setShowBubble(true);
            return;
        }
        const timer = setTimeout(() => {
            setShowBubble(true);
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [isActive, inViewport, delay]);

    // smoothly scrolls the tooltip into the center of the scroll container when it becomes active
    // disabled if autoScroll is false
    useEffect(() => {
        if (!isActive || !autoScroll) return;
        const timer = setTimeout(() => {
            const node = tooltipRef.current as unknown as HTMLElement | null;
            if (node == null) return;
            node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        return () => {
            clearTimeout(timer);
        };
    }, [isActive, autoScroll]);

    const [arrowLeft, setArrowLeft] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!isActive || arrowPercent == null) {
            setArrowLeft(undefined);
            return;
        }
        const frame = requestAnimationFrame(() => {
            const wrapperEl =
                tooltipRef.current as unknown as HTMLElement | null;
            if (wrapperEl == null) return;
            const thumb =
                wrapperEl.querySelector<HTMLElement>('[role="slider"]');
            if (thumb == null) return;
            const wrapperRect = wrapperEl.getBoundingClientRect();
            const thumbRect = thumb.getBoundingClientRect();
            const thumbCenter =
                thumbRect.left + thumbRect.width / 2 - wrapperRect.left;
            const bubbleLeft =
                wrapperRect.width * ((1 - BUBBLE_WIDTH_FRACTION) / 2);
            setArrowLeft(thumbCenter - bubbleLeft - ARROW_SIZE / 2);
        });
        return () => {
            cancelAnimationFrame(frame);
        };
    }, [isActive, arrowPercent]);

    const isBottom = placement === 'bottom';

    const tooltipPositionStyle = isBottom
        ? { top: '100%' as const, marginTop: TOOLTIP_GAP - verticalOffset }
        : {
              bottom: '100%' as const,
              marginBottom: TOOLTIP_GAP - verticalOffset,
          };

    const arrowPositionStyle = isBottom
        ? { top: -ARROW_SIZE / 2 }
        : { bottom: -ARROW_SIZE / 2 };

    return (
        <View
            width="100%"
            ref={tooltipRef}
            style={{
                position: 'relative',
                zIndex: isActive ? 100 : 0,
            }}
        >
            {children}
            <AnimatePresence>
                {showBubble && (
                    <YStack
                        key="tooltip-bubble"
                        position="absolute"
                        left={0}
                        right={0}
                        alignItems="center"
                        zIndex={100}
                        animation="slow"
                        enterStyle={{
                            opacity: 0,
                            y: isBottom ? -8 : 8,
                        }}
                        exitStyle={{
                            opacity: 0,
                            y: isBottom ? -8 : 8,
                        }}
                        animateOnly={['opacity', 'transform']}
                        opacity={1}
                        y={0}
                        {...tooltipPositionStyle}
                    >
                        <YStack
                            backgroundColor="$black"
                            borderRadius={12}
                            padding={12}
                            gap={8}
                            width={`${BUBBLE_WIDTH_FRACTION * 100}%`}
                            position="relative"
                        >
                            <View
                                position="absolute"
                                left={arrowLeft ?? '50%'}
                                marginLeft={
                                    arrowLeft != null ? 0 : -ARROW_SIZE / 2
                                }
                                width={ARROW_SIZE}
                                height={ARROW_SIZE}
                                backgroundColor="$black"
                                zIndex={1}
                                style={{
                                    transform: [{ rotate: '45deg' }],
                                }}
                                {...arrowPositionStyle}
                            />
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                gap={8}
                            >
                                <BodySmallEmphasized color="$white">
                                    {title}
                                </BodySmallEmphasized>
                                <XStack
                                    gap={4}
                                    cursor="pointer"
                                    alignItems="center"
                                    onPress={onNext}
                                >
                                    <BodySmallRegular color="$white">
                                        {isLast ? 'Done' : 'Next'}
                                    </BodySmallRegular>
                                    {!isLast && (
                                        <Icon
                                            icon={ArrowRight}
                                            color="$white"
                                            size={16}
                                        />
                                    )}
                                </XStack>
                            </XStack>
                            <BodySmallRegular color="$white">
                                {content}
                            </BodySmallRegular>
                        </YStack>
                    </YStack>
                )}
            </AnimatePresence>
        </View>
    );
};
